/**
 * Contacts list — URL query vocabulary.
 *
 * Every key here is named after the API parameter it becomes, which is also the
 * name the Segment model stores (`ApiSegmentFilterParams`). One vocabulary now
 * serves three surfaces — the URL, a saved segment, and the request — instead of
 * three hand-written translations that can drift apart.
 *
 * Naming the keys after the API params also makes the pre-existing deep link
 * from Campaign Run History (`?advanced=…&campaignId=…&errorCode=…`) a strict
 * SUBSET of this spec, so it keeps working untouched rather than needing a
 * separate bootstrap path.
 *
 * The filter primitives (`SegmentFilters` and friends) live here rather than in
 * Contacts.tsx so this module has no import back into the page — the page
 * imports from here and re-exports what its tests already reference.
 */
import { ADVANCED_FILTER_OPTIONS } from '@/constants/advancedContactFilters';
import type { SegmentFilterParams as ApiSegmentFilterParams } from '@/services/admin.api';
import { field, UrlState } from '@/hooks/admin/useUrlListState';

export interface AttributeConditionDraft {
  key: string;
  value: string;
}

/** 'all' = no assignment filter, 'unassigned' = no team member assigned, else a userId. */
export const ASSIGNED_TO_ALL = 'all';
export const ASSIGNED_TO_UNASSIGNED = 'unassigned';

/**
 * Every filterable dimension beyond search/status/tags (which stay "live" — see
 * component body), unified into ONE shape. Tier 1 (advancedFilter/errorCode/
 * campaignFilter) used to be its own top-level, always-live state, separate from
 * Tier 2/3's draft-then-Apply state — two different interaction models for
 * conceptually the same thing ("a filter"), which is exactly the UI confusion
 * this shape fixes. Everything here now goes through the SAME committed/draft
 * pair (`filters`/`filterDraft` in the component) and the SAME Apply/Clear gate.
 */
export interface SegmentFilters {
  /** Tier 1 — WhatsApp delivery/engagement status. Still single-select (one at a
   *  time): each option is its own CampaignLog aggregation bucket, not a boolean
   *  toggle, so "pick one" is a real constraint, not just a leftover UI choice. */
  advancedFilter: string;
  /** Only meaningful when advancedFilter === 'failedByError'. */
  errorCode: string;
  /** Only meaningful when advancedFilter === 'neverDeliveredThisCampaign'. */
  campaignFilter: string;
  source: string;
  dateAddedFrom: string;
  dateAddedTo: string;
  engagementTier: string;
  /** "Last message" (either direction) range — YYYY-MM-DD, like dateAdded. */
  lastMessageFrom: string;
  lastMessageTo: string;
  /** "Last note added" range — YYYY-MM-DD, like dateAdded. */
  lastNoteFrom: string;
  lastNoteTo: string;
  frequencyMin: string;
  frequencyMax: string;
  campaignSentFrom: string;
  campaignSentTo: string;
  matchMode: 'all' | 'any';
  attributes: AttributeConditionDraft[];
  assignedTo: string;
  minFailureRate: string;
  minAttempts: string;
}

export function emptySegmentFilters(): SegmentFilters {
  return {
    advancedFilter: 'all',
    errorCode: '',
    campaignFilter: '',
    source: 'all',
    dateAddedFrom: '',
    dateAddedTo: '',
    engagementTier: 'all',
    lastMessageFrom: '',
    lastMessageTo: '',
    lastNoteFrom: '',
    lastNoteTo: '',
    frequencyMin: '',
    frequencyMax: '',
    campaignSentFrom: '',
    campaignSentTo: '',
    matchMode: 'any',
    attributes: [],
    assignedTo: ASSIGNED_TO_ALL,
    minFailureRate: '',
    minAttempts: '',
  };
}

/** Typed as `string[]`, not the literal union: the guard is a RUNTIME membership
 *  check, while `SegmentFilters.advancedFilter` is a plain string — narrowing
 *  here would break the round-trip through it and add no real safety. */
const ADVANCED_FILTER_VALUES: string[] = ADVANCED_FILTER_OPTIONS.map((o) => o.value);

/** Guards the one structured param, so malformed JSON in a hand-edited URL
 *  falls back to "no attribute conditions" instead of reaching render. */
function isAttributeConditions(value: unknown): value is AttributeConditionDraft[] {
  return (
    Array.isArray(value) &&
    value.every(
      (row) =>
        !!row &&
        typeof row === 'object' &&
        typeof (row as AttributeConditionDraft).key === 'string' &&
        typeof (row as AttributeConditionDraft).value === 'string'
    )
  );
}

/**
 * `status` / `contactSource` / `engagementTier` are deliberately unvalidated
 * strings: their option lists carry UI labels and live with the page, the
 * backend is the validating authority either way, and an unrecognised value
 * degrades to an empty result plus a chip showing the raw text (the label
 * lookups all fall back with `opt?.label || value`). Where a shared constant
 * already exists, the value IS constrained.
 */
export const CONTACTS_URL_SPEC = {
  search: field.str(),
  status: field.str('all'),
  contacted: field.enumOf(['all', 'yes', 'no'] as const, 'all'),
  /** 'yes' = has at least one note; 'no' = none. Independent of `contacted`. */
  notes: field.enumOf(['all', 'yes', 'no'] as const, 'all'),
  tags: field.csv(),

  advanced: field.enumOf(ADVANCED_FILTER_VALUES, 'all'),
  errorCode: field.str(),
  campaignId: field.str(),
  contactSource: field.str('all'),
  dateAddedFrom: field.str(),
  dateAddedTo: field.str(),
  engagementTier: field.str('all'),
  lastMessageFrom: field.str(),
  lastMessageTo: field.str(),
  lastNoteFrom: field.str(),
  lastNoteTo: field.str(),
  frequencyMin: field.str(),
  frequencyMax: field.str(),
  campaignSentFrom: field.str(),
  campaignSentTo: field.str(),
  matchMode: field.enumOf(['all', 'any'] as const, 'any'),
  attributes: field.json<AttributeConditionDraft[]>([], isAttributeConditions),
  assignedTo: field.str(ASSIGNED_TO_ALL),
  minFailureRate: field.str(),
  minAttempts: field.str(),

  page: field.page(),
};

export type ContactsUrlState = UrlState<typeof CONTACTS_URL_SPEC>;

/**
 * The drawer, the chip row and the segment serializer all speak `SegmentFilters`
 * (a grouped object), while the URL is flat. These two adapters are the only
 * place that seam exists — every existing call site keeps its current shape.
 */
export function pickSegmentFilters(q: ContactsUrlState): SegmentFilters {
  return {
    advancedFilter: q.advanced,
    errorCode: q.errorCode,
    campaignFilter: q.campaignId,
    source: q.contactSource,
    dateAddedFrom: q.dateAddedFrom,
    dateAddedTo: q.dateAddedTo,
    engagementTier: q.engagementTier,
    lastMessageFrom: q.lastMessageFrom,
    lastMessageTo: q.lastMessageTo,
    lastNoteFrom: q.lastNoteFrom,
    lastNoteTo: q.lastNoteTo,
    frequencyMin: q.frequencyMin,
    frequencyMax: q.frequencyMax,
    campaignSentFrom: q.campaignSentFrom,
    campaignSentTo: q.campaignSentTo,
    matchMode: q.matchMode,
    attributes: q.attributes,
    assignedTo: q.assignedTo,
    minFailureRate: q.minFailureRate,
    minAttempts: q.minAttempts,
  };
}

export function spreadSegmentFilters(f: SegmentFilters): Partial<ContactsUrlState> {
  return {
    advanced: f.advancedFilter,
    errorCode: f.errorCode,
    campaignId: f.campaignFilter,
    contactSource: f.source,
    dateAddedFrom: f.dateAddedFrom,
    dateAddedTo: f.dateAddedTo,
    engagementTier: f.engagementTier,
    lastMessageFrom: f.lastMessageFrom,
    lastMessageTo: f.lastMessageTo,
    lastNoteFrom: f.lastNoteFrom,
    lastNoteTo: f.lastNoteTo,
    frequencyMin: f.frequencyMin,
    frequencyMax: f.frequencyMax,
    campaignSentFrom: f.campaignSentFrom,
    campaignSentTo: f.campaignSentTo,
    matchMode: f.matchMode,
    attributes: f.attributes,
    assignedTo: f.assignedTo,
    minFailureRate: f.minFailureRate,
    minAttempts: f.minAttempts,
  };
}

/**
 * Re-populates every filter control from a stored segment spec — the exact
 * inverse of `toSegmentFilterParamsPayload`. Pure and shared, so loading a saved
 * segment and reading a deep link resolve through the same translation rather
 * than two copies that can disagree about what a filter means.
 */
export function segmentParamsToFilters(p: ApiSegmentFilterParams): SegmentFilters {
  return {
    advancedFilter: p.advanced || 'all',
    errorCode: p.errorCode !== undefined ? String(p.errorCode) : '',
    campaignFilter: p.campaignId || '',
    source: p.contactSource || 'all',
    dateAddedFrom: p.dateAddedFrom || '',
    dateAddedTo: p.dateAddedTo || '',
    engagementTier: p.engagementTier || 'all',
    lastMessageFrom: p.lastMessageFrom || '',
    lastMessageTo: p.lastMessageTo || '',
    lastNoteFrom: p.lastNoteFrom || '',
    lastNoteTo: p.lastNoteTo || '',
    frequencyMin: p.frequencyMin !== undefined ? String(p.frequencyMin) : '',
    frequencyMax: p.frequencyMax !== undefined ? String(p.frequencyMax) : '',
    campaignSentFrom: p.campaignSentFrom || '',
    campaignSentTo: p.campaignSentTo || '',
    matchMode: p.matchMode || 'any',
    attributes: (p.attributes || []).map((a) => ({ key: a.key, value: String(a.value) })),
    assignedTo: p.assignedTo || ASSIGNED_TO_ALL,
    minFailureRate: p.minFailureRate !== undefined ? String(p.minFailureRate) : '',
    minAttempts: p.minAttempts !== undefined ? String(p.minAttempts) : '',
  };
}
